import React, { useEffect, useRef, useState } from "react";
import Layout from "../../components/layout";
import {
  FaArrowLeft,
  FaFileInvoice,
  FaSearch,
  FaSpinner,
} from "react-icons/fa";
import { MdCheckBoxOutlineBlank } from "react-icons/md";
import { ImPrinter } from "react-icons/im";

import styles from "./OpenOrderListPage.module.scss";
import { Link, useNavigate } from "react-router-dom";
import { route } from "../../static/route";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchOpenOrdersData,
  fetchPartiesData,
  getOrders,
  getParties,
  getSelectedOrder,
  getSelectedParty,
  getTotalOrders,
  updateSelectedOrder,
  updateSelectedParty,
} from "../../features/openOrder/openOrderSlice";
import ListCard from "../../components/orders/ListCard";
import { ICloseFormInput, OptionsProps, OrderProps } from "../../types/order";
import Select from "react-select";
import { selectStyles } from "../../static/styles";
import { FaSquareCheck } from "react-icons/fa6";
import OrderPdf from "../../components/orderDetails/OrderPdf";
import { useReactToPrint } from "react-to-print";
import { POST } from "../../services/axios.service";
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";

const printStyles = `
  @media print {
    @page {
      size: A6 portrait;
      margin: 0;
    }
  }
`;

const OpenOrderListPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  let debounceTimeout: NodeJS.Timeout;
  const printRef = useRef<HTMLDivElement>(null);
  const orders = useAppSelector(getOrders);
  const totalOrders = useAppSelector(getTotalOrders);
  const parties = useAppSelector(getParties);
  const selectedParty = useAppSelector(getSelectedParty);
  const selectedOrder = useAppSelector(getSelectedOrder);

  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [firstLoading, setFirstLoading] = useState<boolean>(false);
  const [partiesOption, setPartiesOption] = useState<OptionsProps[]>([]);
  const [printOrders, setPrintOrders] = useState<OrderProps[]>([]);
  const [invoiceOrders, setInvoiceOrders] = useState<OrderProps[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const methods = useForm<ICloseFormInput>();

  const { handleSubmit, control, reset } = methods;

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "orders",
  });

  const onSubmit: SubmitHandler<ICloseFormInput> = async (data) => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      await POST("/v1/worker/order-close", data)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            await POST(`/v1/worker/payments-reports`, {
              payment_id: response.data.payment_id,
            })
              .then(async (response: any) => {
                if (response.data.status === "success") {
                  setInvoiceOrders([]);
                  reset({ orders: [] });

                  if (page === 1) {
                    dispatch(
                      fetchOpenOrdersData({
                        page: page,
                        party_id: selectedParty ? selectedParty : undefined,
                        order_id: selectedOrder ? selectedOrder : undefined,
                      })
                    );
                  } else {
                    setPage(1);
                  }

                  const downloadLink = response.data.data.download_link;
                  try {
                    const newWindow = window.open(downloadLink, "_blank");
                    if (
                      !newWindow ||
                      newWindow.closed ||
                      typeof newWindow.closed == "undefined"
                    ) {
                      throw new Error("પોપ્અપ બ્લોક કરેલ છે.");
                    }
                  } catch (e) {
                    window.location.href = downloadLink;
                  }
                } else {
                  setErrorMessage(response.data.message);
                }
              })
              .catch((error: any) => {
                setErrorMessage(error.data.message);
              });
          } else {
            setErrorMessage(response.data.message);
          }
        })
        .catch((error: any) => {
          setErrorMessage(error.data.message);
        });
    } catch (error) {
      setErrorMessage("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.");
    }

    setIsLoading(false);
  };

  const handleGoToBack = () => {
    navigate(route.dashboard);
  };

  const handleChangeParty = (id: number) => {
    setErrorMessage(null);
    dispatch(updateSelectedParty(id));
    setPage(1);
  };

  const handleChangeOrderId = (id: number | null) => {
    dispatch(updateSelectedOrder(id));
    setPage(1);
  };

  const handleChangePrintOrder = (order: OrderProps, action: boolean) => {
    setPrintOrders((prevOrders) => {
      if (action) {
        if (
          !prevOrders.some((existingOrder) => existingOrder.id === order.id)
        ) {
          return [...prevOrders, order];
        }
        return prevOrders;
      } else {
        return prevOrders.filter(
          (existingOrder) => existingOrder.id !== order.id
        );
      }
    });
  };

  const handleChangeInvoiceOrder = (order: OrderProps, action: boolean) => {
    setErrorMessage(null);

    if (selectedParty <= 0) {
      setErrorMessage("કૃપા કરીને પાર્ટી નું નામ સિલેક્ટ કરો.");
      return;
    }

    setInvoiceOrders((prevOrders) => {
      if (action) {
        if (
          !prevOrders.some((existingOrder) => existingOrder.id === order.id)
        ) {
          append({
            order_id: order.id,
            delivered_qty: order.delivered_qty,
            delivered_weight: order.delivered_weight || 0,
          });
          return [...prevOrders, order];
        }
        return prevOrders;
      } else {
        const index = fields.findIndex((field) => field.order_id === order.id);
        if (index >= 0) remove(index);

        return prevOrders.filter(
          (existingOrder) => existingOrder.id !== order.id
        );
      }
    });
  };

  const handlePrintReport = async () => {
    setErrorMessage(null);
    try {
      await POST(`/v1/worker/orders-reports`, {
        order_ids: printOrders.map((order) => order.id) || [],
      })
        .then(async (response: any) => {
          if (response.data.status === "success") {
            setPrintOrders([]);
            const downloadLink = response.data.data.download_link;
            try {
              const newWindow = window.open(downloadLink, "_blank");
              if (
                !newWindow ||
                newWindow.closed ||
                typeof newWindow.closed == "undefined"
              ) {
                throw new Error("પોપ્અપ બ્લોક કરેલ છે.");
              }
            } catch (e) {
              window.location.href = downloadLink;
            }
          } else {
            setErrorMessage(response.data.message);
          }
        })
        .catch((error: any) => {
          setErrorMessage(error.data.message);
        });
    } catch (error) {
      setErrorMessage("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.");
    }
  };

  useEffect(() => {
    if (page === 1) setFirstLoading(true);
    setIsLoading(true);

    dispatch(
      fetchOpenOrdersData({
        page: page,
        party_id: selectedParty ? selectedParty : undefined,
        order_id: selectedOrder ? selectedOrder : undefined,
      })
    ).then(() => {
      setIsLoading(false);
      setFirstLoading(false);
    });
  }, [page, selectedParty, selectedOrder]);

  useEffect(() => {
    dispatch(fetchPartiesData());
  }, []);

  useEffect(() => {
    if (parties) {
      const options: OptionsProps[] = [];
      parties?.map((party) => {
        options.push({
          value: party.id,
          label:
            party.firm_name +
            (party.name ? " - " + party.name : "") +
            (party.phone ? " (" + party.phone + ")" : ""),
        });
      });
      setPartiesOption(options);
    }
  }, [parties]);

  return (
    <Layout>
      <div className={styles.OpenOrderListPage}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="w-full px-10 h-[200px] text-center flex flex-col justify-center relative">
              <FaArrowLeft
                onClick={handleGoToBack}
                className="absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
              />

              <h1 className="text-4xl mt-6 font-bold text-white">ચાલુ કામ</h1>

              <div className="absolute top-5 right-5 flex gap-3">
                {printOrders.length > 0 && (
                  <ImPrinter
                    onClick={handlePrintReport}
                    className="text-3xl text-white"
                  />
                )}

                <button
                  className={`${
                    invoiceOrders.length > 0 && !isLoading ? "inline" : "hidden"
                  }`}
                  disabled={invoiceOrders.length === 0 || isLoading}
                  type="submit"
                >
                  <FaFileInvoice className="text-3xl text-white" />
                </button>

                {invoiceOrders.length > 0 && isLoading && (
                  <div className="flex items-center justify-center">
                    <FaSpinner className="text-3xl text-white animate-spin" />
                  </div>
                )}
              </div>
            </div>

            <div className="w-full relative mt-[70px] px-10 py-5 lg:mt-[20px]">
              {errorMessage && (
                <p className="text-red-500 text-sm font-semibold text-center mb-5">
                  {errorMessage}
                </p>
              )}

              <div className="flex mb-5">
                <button
                  className="p-2 w-fit ml-auto rounded-md flex items-center bg-gradient-to-r from-primary-1 to-primary-2 text-white transition-colors duration-300"
                  onClick={() => setShowFilter(!showFilter)}
                  type="button"
                >
                  <FaSearch className="mr-2" />{" "}
                  {showFilter ? "ફિલ્ટર છુપાવો" : "ફિલ્ટર બતાવો"}
                </button>
              </div>

              {showFilter && (
                <>
                  <div className="bg-gradient-to-r mb-5 from-primary-1 to-primary-2 h-16 w-full relative rounded">
                    <Select
                      isClearable
                      value={partiesOption.find(
                        (val) => val.value === selectedParty
                      )}
                      className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      styles={selectStyles}
                      placeholder={"પાર્ટી નું નામ દાખલ કરો"}
                      options={partiesOption}
                      onChange={(val: any) => handleChangeParty(val?.value)}
                    />
                  </div>
                  <div className="bg-gradient-to-r mb-10 from-primary-1 to-primary-2 h-16 w-full relative rounded">
                    <input
                      defaultValue={selectedOrder || ""}
                      className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      placeholder="કામ નું આઇડી દાખલ કરો"
                      pattern="[0-9]+(\.[0-9]+)?"
                      type="number"
                      onKeyUp={(e: any) => {
                        const value = e.target.value;

                        clearTimeout(debounceTimeout);
                        debounceTimeout = setTimeout(() => {
                          handleChangeOrderId(value || null);
                        }, 2000);
                      }}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                </>
              )}

              {firstLoading ? (
                <p className="mt-4 text-primary-2 font-bold text-2xl text-center">
                  રાહ જુઓ...
                </p>
              ) : (
                <>
                  {orders && orders.length > 0 ? (
                    <div>
                      {orders.map((order, index) => (
                        <div className="relative w-full h-full" key={index}>
                          <ListCard
                            order={order}
                            index={fields.findIndex(
                              (o) => o.order_id === order.id
                            )}
                          />

                          <div className="absolute top-3 right-3 flex gap-3">
                            {printOrders.some((o) => o.id === order.id) ? (
                              <div>
                                <ImPrinter className="text-xl text-primary-1" />
                                <FaSquareCheck
                                  onClick={(e: any) => {
                                    e.preventDefault();
                                    handleChangePrintOrder(order, false);
                                  }}
                                  className="text-xl text-primary-1"
                                />
                              </div>
                            ) : (
                              <div>
                                <ImPrinter className="text-xl text-primary-1" />
                                <MdCheckBoxOutlineBlank
                                  onClick={(e: any) => {
                                    e.preventDefault();
                                    handleChangePrintOrder(order, true);
                                  }}
                                  className="text-xl text-primary-1"
                                />
                              </div>
                            )}

                            {invoiceOrders.some((o) => o.id === order.id) ? (
                              <div>
                                <FaFileInvoice className="text-xl text-primary-1" />
                                <FaSquareCheck
                                  onClick={(e: any) => {
                                    e.preventDefault();
                                    handleChangeInvoiceOrder(order, false);
                                  }}
                                  className="text-xl text-primary-1"
                                />
                              </div>
                            ) : (
                              <div>
                                <FaFileInvoice className="text-xl text-primary-1" />
                                <MdCheckBoxOutlineBlank
                                  onClick={(e: any) => {
                                    e.preventDefault();
                                    handleChangeInvoiceOrder(order, true);
                                  }}
                                  className="text-xl text-primary-1"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="mt-4 text-primary-2 font-bold text-2xl">
                        હાલ કોઈ કામ ની વિગતો મળી નથી.
                      </p>

                      <div className="w-full flex justify-center">
                        <Link
                          to={route.newOrder}
                          state={
                            selectedParty ? { partyId: selectedParty } : undefined
                          }
                        >
                          <button
                            type="button"
                            className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                          >
                            કામ ઉમેરો
                          </button>
                        </Link>
                      </div>
                    </div>
                  )}

                  {totalOrders > 0 && totalOrders > orders.length && (
                    <div className="flex">
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={isLoading}
                        type="button"
                        className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                      >
                        {isLoading ? "રાહ જુઓ..." : "વધુ જુઓ"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </form>
        </FormProvider>
      </div>

      <div ref={printRef} className="print:grid hidden">
        <style>{printStyles}</style>

        {printOrders.map((order, index) => (
          <OrderPdf order={order} key={index} />
        ))}
      </div>
    </Layout>
  );
};
export default OpenOrderListPage;
